<template>
  <div>
    <div
      v-if="userMetaClass"
      class="panel m-3"
    >
      <p class="panel-heading">
        Stammdaten
      </p>
      <div class="container pt-3 pb-3">
        <div class="stammdaten">
          <b-image
            class="stammdaten-avatar"
            :src="dropFileURL"
            alt="Profilbild"
            rounded
          />
          <div class="stammdaten-facts">
            <p class="title is-5 mb-2">
              {{ factsLine }}
            </p>
            <div class="facts-columns mb-2">
              <div>
                <p v-if="birthday">
                  Geburtstag: {{ birthday.toLocaleDateString("de-DE") }}
                </p>
                <p v-if="func">
                  Funktion: {{ func }}
                </p>
              </div>
              <div v-if="filteredGroups.length > 0">
                <p
                  v-for="group in filteredGroups"
                  :key="group.id"
                >
                  <a @click="openGroup(group)">
                    {{ group.attributes.group_name }}
                  </a>
                </p>
              </div>
            </div>
            <template v-if="hasContactData">
              <hr class="my-2">
              <div>
                <p v-if="email">
                  Email: <a :href="`mailto:${email}`">{{ email }}</a>
                </p>
                <p v-if="phone1Line">
                  {{ phone1Line }}
                </p>
                <p v-if="phone2Line">
                  {{ phone2Line }}
                </p>
                <p v-if="addressLine">
                  {{ addressLine }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="contactChildren.length > 0"
      class="panel m-3"
    >
      <p class="panel-heading">
        Kontaktkinder
      </p>
      <div class="container pt-3 pb-3">
        <b-table
          class="client-table"
          :data="contactChildren"
          narrowed
          striped
          hoverable
          @click="openClient"
        >
          <b-table-column
            v-slot="props"
            label="Name"
            cell-class="has-text-left"
          >
            {{ props.row.attributes.first_name }}
            {{ props.row.attributes.last_name }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            label="Gruppe"
            cell-class="has-text-left"
          >
            {{ props.row.attributes.group && props.row.attributes.group.attributes
              ? props.row.attributes.group.attributes.group_name : "" }}
          </b-table-column>
        </b-table>
      </div>
    </div>

    <section class="mx-5 my-2">
      <div
        class="buttons"
        style="display: flex"
      >
        <b-button
          class="is-primary"
          tag="router-link"
          to="/users"
        >
          Zurück
        </b-button>
        <b-button
          class="is-danger"
          style="margin-left: auto"
          @click="isDeleteCheck = true"
        >
          Löschen
        </b-button>
        <b-button
          class="is-primary"
          @click="triggerEdit"
        >
          Bearbeiten
        </b-button>
      </div>
    </section>
    <ModalComponent
      v-if="isDeleteCheck && userMetaClass"
      :item="`${firstName} ${familyName}`"
      :is-delete-check="isDeleteCheck"
      :is-loading="isLoading"
      :cancel="() => isDeleteCheck = false"
      :submit="submitDelete"
    />
  </div>
</template>

<script>
/**
 * This file is for viewing a focused user
 */
import Parse from "parse/dist/parse.min.js";
import {getUserPicture} from "../../lib/Picture";
import {queryGroups, queryUserMetaClasses} from "@/lib/Query";
import {negativeFeedBack, positiveFeedBack} from "@/lib/Toast";
import ModalComponent from "@/components/helperComponents/ModalComponent";
import {mapGetters} from "vuex";

export default {
	name: "ViewUser",
	components: {ModalComponent},
	data() {
		return {
			isDeleteCheck: false,
			isLoading: false,
			dropFile: undefined,
			firstName: "",
			familyName: "",
			birthday: new Date(),
			func: "",
			groupId: "",
			gender: "",
			email: "",
			phone1: "",
			phone2: "",
			location: "",
			street: "",
			plz: "",
			groupName: "",
			userMetaClass: undefined,
			userGroups: [],
			chosenClients: [],
			info1: "",
			info2: ""
		};
	},
	computed: {
		...mapGetters({
			currentUserMeta: "getCurrentUserMeta"
		}),
		age() {
			if (!this.birthday) return undefined;
			const today = new Date();
			let age = today.getFullYear() - this.birthday.getFullYear();
			const monthDiff = today.getMonth() - this.birthday.getMonth();
			if (monthDiff < 0 ||
				(monthDiff === 0 && today.getDate() < this.birthday.getDate()))
			{
				age--;
			}
			return age;
		},
		factsLine() {
			const parts = [`${this.firstName} ${this.familyName}`.trim()];
			if (this.age !== undefined) parts.push(String(this.age));
			if (this.gender) parts.push(this.gender);
			return parts.join(", ");
		},
		phone1Line() {
			return this.formatPhone(this.phone1, this.info1);
		},
		phone2Line() {
			return this.formatPhone(this.phone2, this.info2);
		},
		addressLine() {
			const city = [this.plz, this.location].filter(Boolean).join(" ");
			return [this.street, city].filter(Boolean).join(", ");
		},
		hasContactData() {
			return !!(this.email || this.phone1Line || this.phone2Line ||
				this.addressLine);
		},
		filteredGroups() {
			return this.userGroups.filter(Boolean);
		},
		contactChildren() {
			// attributes.clients may hold pointers to deleted or otherwise
			// unresolvable clients, which would render as blank rows.
			return this.chosenClients.filter(c =>
				c && c.attributes && c.attributes.first_name);
		},
		dropFileURL() {
			const pictureObject = {
				newProfilePicture: typeof this.dropFile === "string" ? undefined : this.dropFile,
				userPicture: typeof this.dropFile === "string" ? this.dropFile : undefined,
				userGender: this.userMetaClass.attributes.gender
			}
			return getUserPicture(pictureObject)
		},
	},
	async created() {
		const users = await queryUserMetaClasses()
		let userMetaClass = this.$route.params.data
		// Reached via browser navigation (back/forward, reload, deep
		// link): the in-memory object is gone, look it up by the URL id.
		if (!userMetaClass && this.$route.params.id) {
			userMetaClass = users.find(u => u.id === this.$route.params.id)
		}
		this.userMetaClass = userMetaClass
		if(!this.userMetaClass){
			await this.$router.push("/users")
			return
		}
		this.$store.dispatch("setDetailTitle",
			`${userMetaClass.attributes.first_name} ${userMetaClass.attributes.last_name}`)
		if (userMetaClass) {
			this.firstName = userMetaClass.attributes.first_name ? userMetaClass.attributes.first_name.trim() : ""
			this.familyName = userMetaClass.attributes.last_name ? userMetaClass.attributes.last_name.trim() : ""
			this.street = userMetaClass.attributes.street ? userMetaClass.attributes.street.trim() : ""
			this.birthday = userMetaClass.attributes.birth_date ? userMetaClass.attributes.birth_date : undefined
			this.plz = userMetaClass.attributes.plz ? userMetaClass.attributes.plz.trim() : ""
			this.location = userMetaClass.attributes.location ? userMetaClass.attributes.location.trim() : ""
			this.gender = userMetaClass.attributes.gender ? userMetaClass.attributes.gender.trim() : ""
			this.email = userMetaClass.attributes.email ? userMetaClass.attributes.email.trim() : ""
			this.phone1 = userMetaClass.attributes.phone1 ? userMetaClass.attributes.phone1.trim() : ""
			this.phone2 = userMetaClass.attributes.phone2 ? userMetaClass.attributes.phone2.trim() : ""
			this.info1 = userMetaClass.attributes.info1 ? userMetaClass.attributes.info1.trim() : ""
			this.info2 = userMetaClass.attributes.info2 ? userMetaClass.attributes.info2.trim() : ""
			this.dropFile = userMetaClass.attributes.profile_picture ? userMetaClass.attributes.profile_picture._url : undefined
			const groupsUser = userMetaClass.attributes.groups ? userMetaClass.attributes.groups : []
			this.func = userMetaClass.attributes.function ? userMetaClass.attributes.function.trim() : ""
			this.chosenClients = userMetaClass.attributes.clients ? userMetaClass.attributes.clients : []
			const groups = await queryGroups()
			for (const group of groupsUser) {

				this.userGroups.push(groups.find(groupOfGroups => groupOfGroups.id === group.id))
			}

		}
	},
	methods: {
		formatPhone(phone, info) {
			phone = phone ? phone.trim() : "";
			info = info ? info.trim() : "";
			if (!phone) return "";
			return info ? `${phone} (${info})` : phone;
		},
		openGroup(group) {
			this.$router.push(
				{ name: "ViewGroup", params: { id: group.id, data: group } });
		},
		openClient(client) {
			// A click that ends a drag-selection selects text, it should
			// not navigate.
			const selection = window.getSelection && window.getSelection()
			if (selection && selection.toString().length > 0) return
			this.$router.push(
				{ name: "ViewClient", params: { id: client.id, data: client } });
		},
		triggerEdit() {
			this.$router.push({ name: "AddUser", params: { data: this.userMetaClass } });
		},
		async submitDelete() {
			this.isLoading = true;
			try {
				this.userMetaClass.set("deleted", true);
				await this.userMetaClass.save();
				positiveFeedBack("Mitarbeiter gelöscht!");
				if (this.userMetaClass.id === this.currentUserMeta.id) {
					await Parse.User.logOut();
					this.isDeleteCheck = false;
					return await this.$router.push("/login");
				}
				this.isDeleteCheck = false;
				await this.$router.push("/users");
			}
			catch (e) {
				negativeFeedBack("Mitarbeiter nicht gelöscht!");
				console.error(e);
			}
			finally {
				this.isLoading = false;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.stammdaten {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	padding-left: 1.5rem;
}

.stammdaten-avatar {
	width: 8rem;
	flex-shrink: 0;
}

/* Crop non-square photos into a circle instead of an ellipse. */
.stammdaten-avatar ::v-deep img {
	width: 8rem;
	height: 8rem;
	object-fit: cover;
}

.stammdaten-facts {
	text-align: left;
}

.stammdaten-facts p {
	margin-bottom: 0.25rem;
}

.facts-columns {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 0.25rem 3.5rem;
}

.client-table ::v-deep tbody tr {
	cursor: pointer;
}

@media screen and (max-width: 768px) {
	.client-table {
		padding: 0 0.75rem;
	}
}
</style>
